import React, { useState, useEffect } from 'react';
import { XCircle } from 'lucide-react';
import { doc, onSnapshot, query, collection, where, getDocs } from 'firebase/firestore';

// Shared UI
import { BlackboxLoaderIcon } from '../../components/SharedUI';

// Feature Components
// Note: Ensure DossierTab is extracted to this directory as well.
import DossierTab from './DossierTab'; 

const PublicDossierPage = ({ projectId, db, setNotification }) => {
    const [dossierData, setDossierData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!projectId || !db) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const docRef = doc(db, 'film_data', projectId);

        // Listen for real-time updates
        const unsubscribe = onSnapshot(docRef, async (docSnap) => {
            try {
                if (docSnap.exists() && docSnap.data().is_dossier_public) {
                    const data = { id: docSnap.id, ...docSnap.data() };

                    // Fetch associated campaigns
                    const campaignsQuery = query(collection(db, 'campaigns'), where('film_id', '==', projectId));
                    const campaignDocs = await getDocs(campaignsQuery);
                    const publicCampaigns = campaignDocs.docs.map(campaignDoc => ({ id: campaignDoc.id, ...campaignDoc.data() }));

                    const formattedData = {
                        filmData: data,
                        visualAnalysis: data.visual_analysis,
                        posterA: data.poster_a,
                        posterB: data.poster_b,
                        videoAnalysis: data.video_analysis,
                        budgetPlan: data.budget_plan,
                        revenueForecast: data.revenue_forecast,
                        pressStrategy: data.press_strategy,
                        campaigns: publicCampaigns,
                        isPrintEnabled: data.is_dossier_public_print || false,
                    };
                    setDossierData(formattedData);
                } else {
                    setNotification({ type: 'error', message: 'This dossier is not public or does not exist.' });
                    setDossierData(null);
                }
            } catch (error) {
                console.error("Public Dossier data processing error:", error);
                setNotification({ type: 'error', message: `Failed to process dossier data: ${error.message}` });
                setDossierData(null);
            } finally {
                setLoading(false);
            }
        }, (error) => {
            console.error("Public Dossier listener error:", error);
            setNotification({ type: 'error', message: `Failed to listen for updates: ${error.message}` });
            setDossierData(null);
            setLoading(false);
        });

        return () => unsubscribe();

    }, [projectId, db, setNotification]);

    if (loading) {
        return (
            <div className="dark font-poppins">
                <div className="bg-zinc-950 min-h-screen flex flex-col items-center justify-center">
                    <BlackboxLoaderIcon className="w-24 h-24" variant="white-pulse" />
                </div>
            </div>
        );
    }

    if (!dossierData) {
        return (
            <div className="dark font-poppins">
                <div className="bg-zinc-950 min-h-screen flex flex-col items-center justify-center text-center p-8">
                    <XCircle className="w-20 h-20 text-red-500 mb-4"/>
                    <h1 className="text-4xl font-bold text-white font-poppins">Access Denied</h1>
                    <p className="text-zinc-400 mt-2">This dossier is not available for public viewing.</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="dark font-poppins bg-zinc-950 min-h-screen">
             <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300&family=poppins:wght@400;700;900&display=swap');
                html { scroll-behavior: smooth; }
                body { background-color: #18181b; } /* Ensures dark background */
                .font-poppins { font-family: 'Poppins', sans-serif; }
                .font-poppins { font-family: 'poppins', sans-serif; }
                .font-poppins-light-italic { font-family: 'Poppins', sans-serif; font-weight: 300; font-style: italic; }
            `}</style>
            <DossierTab allData={dossierData} setNotification={setNotification} isPublicView={true} />
        </div>
    );
};

export default PublicDossierPage;